// Features: MCRO001-F0037, MCRO001-F0038 (inherit MCRO001-F0007/F0008/F0027/F0029)
// Tests: T-MCRO-037, T-MCRO-038
//
// Pure model helpers for the Rehearsal studio stage and drill controls. Nothing in
// this module reads or writes a Sheet, revision, timing map, rail priority or media
// position: every function either resolves an existing authored bar/section to one
// exact count, or formats a value another Feature already computed. Count remains the
// sole clock authority (MCRO001-D006 §3).

import { DEFAULT_FOCUS_ON_RATE_THRESHOLD } from './demonstrationPriority';

export const MIRROR_STORAGE_KEY = 'mc.rehearsalMirror';

// MCRO001-F0037 case B: a view preference, not routine state. Storage is optional —
// a private-mode/quota/corrupt read must fall back to "mirror off" rather than throw.
export function readMirrorPreference(storage = typeof localStorage === 'undefined' ? null : localStorage) {
  try {
    return storage?.getItem(MIRROR_STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

export function writeMirrorPreference(mirrored, storage = typeof localStorage === 'undefined' ? null : localStorage) {
  if (!storage || typeof storage.setItem !== 'function') return false;
  try {
    storage.setItem(MIRROR_STORAGE_KEY, mirrored ? '1' : '0');
    return true;
  } catch (_) {
    return false;
  }
}

// MCRO001-F0038 case A: the complete authored eight-count bar containing the count.
// `bars` is the routine-wide ordered vocabulary rehearsalBarOptions() already builds
// for the Start/End 8-beat bar selectors, so A/B marking can never resolve to a bar
// the selectors do not offer.
export function barContainingCount(bars = [], count) {
  const target = Number(count);
  if (!Number.isFinite(target)) return null;
  return bars.find((bar) => target >= Number(bar.startCount) && target <= Number(bar.endCount)) || null;
}

const sameRange = (left, right) => Number(left?.startCount) === Number(right?.startCount)
  && Number(left?.endCount) === Number(right?.endCount);

// MCRO001-F0038 cases A/C: bind one loop edge to the authored bar containing the live
// count. Returns null — refuse, leaving the previous range untouched — when the count
// is outside every authored bar, or when the result would reverse the range. It never
// clamps silently to a range the user did not ask for.
export function markLoopEdge(bars = [], loop = null, edge = 'start', count = 1) {
  const bar = barContainingCount(bars, count);
  if (!bar || !loop) return null;
  const next = edge === 'end'
    ? { startCount: Number(loop.startCount), endCount: Number(bar.endCount) }
    : { startCount: Number(bar.startCount), endCount: Number(loop.endCount) };
  if (!Number.isFinite(next.startCount) || !Number.isFinite(next.endCount)) return null;
  if (next.endCount < next.startCount) return null;
  if (sameRange(next, loop)) return null;
  return next;
}

// MCRO001-F0038 case B: move one loop edge by exactly one entry in the routine-wide
// authored bar order — never by a raw count. Refuses (null) past either end of the
// routine and refuses any reversal.
export function nudgeLoopEdge(bars = [], loop = null, edge = 'start', direction = 1) {
  if (!loop) return null;
  const step = Number(direction) >= 0 ? 1 : -1;
  const index = edge === 'end'
    ? bars.findIndex((bar) => Number(bar.endCount) === Number(loop.endCount))
    : bars.findIndex((bar) => Number(bar.startCount) === Number(loop.startCount));
  if (index < 0) return null;
  const target = bars[index + step];
  if (!target) return null;
  const next = edge === 'end'
    ? { startCount: Number(loop.startCount), endCount: Number(target.endCount) }
    : { startCount: Number(target.startCount), endCount: Number(loop.endCount) };
  if (next.endCount < next.startCount) return null;
  return next;
}

export function canNudgeLoopEdge(bars = [], loop = null, edge = 'start', direction = 1) {
  return nudgeLoopEdge(bars, loop, edge, direction) !== null;
}

const orderedSections = (sections = []) => [...sections]
  .filter((section) => Number.isFinite(Number(section?.startCount)))
  .sort((left, right) => Number(left.startCount) - Number(right.startCount));

// MCRO001-F0038 case E: the count of the previous/next authored section boundary.
// Backwards first returns the current section's own start when the count is already
// past it — the behaviour every media transport has — and only then steps to the
// previous section. Returns null when there is nowhere to go, so the control can be
// honestly disabled rather than being a live no-op.
export function sectionJumpTarget(sections = [], count = 1, direction = 1) {
  const ordered = orderedSections(sections);
  if (!ordered.length) return null;
  const target = Number(count);
  if (!Number.isFinite(target)) return null;
  if (Number(direction) >= 0) {
    const next = ordered.find((section) => Number(section.startCount) > target);
    return next ? Number(next.startCount) : null;
  }
  const current = ordered.filter((section) => Number(section.startCount) <= target).pop();
  if (current && Number(current.startCount) < target) return Number(current.startCount);
  const previous = ordered.filter((section) => Number(section.startCount) < target).pop();
  return previous ? Number(previous.startCount) : null;
}

// MCRO001-F0038 case F: discrete presets over the existing MCRO001-F0008 0.50-1.50
// selected rate. They are values for the existing control, not a second rate model.
export const REHEARSAL_SPEED_PRESETS = Object.freeze([0.5, 0.75, 1]);

export function resolveFocusOnRateThreshold(threshold) {
  const value = Number(threshold);
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_FOCUS_ON_RATE_THRESHOLD;
}

// MCRO001-F0038 case G: observational only. It reports the effective video rate
// MCRO001-F0027 already sends upward against the threshold MCRO001-F0029 already
// applies, so a dancer can see how much headroom is left before video is withheld.
// It classifies nothing, withholds nothing and returns '' the moment the backdrop is
// not visible, so it can never show a stale rate.
export function videoRateReadout(projection = null, threshold = null) {
  if (!projection?.visible) return '';
  const rate = projection.effectiveVideoRate;
  // `Number(null)` is a finite 0, so an explicitly null rate must be rejected before
  // the numeric check — reporting "0%" would be exactly the stale/lying readout the
  // fail-closed contract forbids.
  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) return '';
  const limit = resolveFocusOnRateThreshold(threshold);
  const parts = [`Video playing at ${Math.round(rate * 100)}%`, `limit ${Math.round(limit * 100)}%`];
  if (projection.fallback) parts.push('frame projection');
  return parts.join(' · ');
}
